// File: client_serena/src/components/MealInfoModal.js

import React from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
  Text, Badge, VStack, Divider, Wrap, WrapItem, Box, HStack, Image
} from "@chakra-ui/react";
import { useAuth } from "../hooks/useAuth";

function MealInfoModal({ meal, isOpen, onClose }) {
  const { user } = useAuth();
  const isSubscribed = user?.subscriptionStatus === "subscribed";
  if (!meal) return null;

  const {
    name = "Unnamed Meal",
    image,
    description,
    ingredients = [],
    macros = {},
    personalizedMacros = {},
    costToMake = 0,
    category = "N/A",
    tags = [],
    dietTags = [],
    genomeCautions = [],
    biomarkerCautions = [],
  } = meal;

  const costPerPortion = Math.round(costToMake / 2) || 0;
  const { carbs = 0, protein = 0, fat = 0 } = macros;
  const { carbs: pc = 0, protein: pp = 0, fat: pf = 0 } = personalizedMacros;

  // Tag display for modal
  const warningTags = [...genomeCautions, ...biomarkerCautions];
  const allTags = [
    ...warningTags.map((tag) => ({ tag, color: "red" })),
    ...dietTags.map((tag) => ({ tag, color: "purple" })),
    ...tags.map((tag) => ({ tag, color: "blue" })),
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="2xl" boxShadow="2xl" p={0}>
        <ModalHeader bgGradient="linear(to-r, green.400, green.600)" color="white" borderTopRadius="2xl" fontWeight="extrabold" fontSize="xl" textAlign="center">
          {name}
        </ModalHeader>
        <ModalCloseButton color="white" top={3} right={3} />
        <ModalBody p={6}>
          <VStack align="start" spacing={4} w="100%">
            {image && (
              <Image
                src={image}
                alt={name}
                borderRadius="xl"
                objectFit="cover"
                w="100%"
                h="200px"
                boxShadow="md"
                mb={2}
              />
            )}
            <HStack w="100%" justify="space-between">
              <Text fontSize="sm" color="gray.600">
                🍽️ <b>Category:</b> {category}
              </Text>
              <Text fontSize="sm" color="green.700" fontWeight="bold">
                💰 ₦{costPerPortion.toLocaleString()} /portion
              </Text>
            </HStack>
            {description && (
              <Text fontSize="md" color="gray.700" mb={1}>
                {description}
              </Text>
            )}
            {isSubscribed && (
              <Box mt={2} bg="purple.50" p={3} borderRadius="md" w="100%" boxShadow="sm">
                <Text fontWeight="bold" mb={1} color="purple.700">🌟 Premium Personalization</Text>
                <Text fontSize="sm" color="gray.700">
                  This meal was matched using deeper analysis including your genome and advanced dietary goals.
                </Text>
                <Text fontSize="sm" color="gray.600" mt={1}>
                  As a subscriber, you also get unlimited smart shuffles, detailed nutrition reports, and more.
                </Text>
              </Box>
            )}
            <Divider />
            <Text fontSize="md" fontWeight="bold" color="gray.800">🧂 Ingredients</Text>
            <Wrap>
              {ingredients.length > 0
                ? ingredients.map((ing, i) => (
                    <WrapItem key={i}>
                      <Badge colorScheme="teal" borderRadius="md" px={2} fontSize="xs" boxShadow="sm">{ing}</Badge>
                    </WrapItem>
                  ))
                : <Text fontSize="sm" color="gray.400">Not specified</Text>}
            </Wrap>
            <Divider />
            <Text fontSize="md" fontWeight="bold" color="gray.800">🔬 Macros per Portion</Text>
            <HStack spacing={6}>
              <Text fontSize="sm" color="gray.600">Carbs: <b>{Math.round(carbs)}g</b></Text>
              <Text fontSize="sm" color="gray.600">Protein: <b>{Math.round(protein)}g</b></Text>
              <Text fontSize="sm" color="gray.600">Fat: <b>{Math.round(fat)}g</b></Text>
            </HStack>
            {personalizedMacros && (
              <>
                <Text fontSize="md" fontWeight="bold" color="purple.700" mt={2}>🎯 Your Macro Targets</Text>
                <HStack spacing={6}>
                  <Text fontSize="sm" color="purple.700">Carbs: <b>{Math.round(pc)}g</b></Text>
                  <Text fontSize="sm" color="purple.700">Protein: <b>{Math.round(pp)}g</b></Text>
                  <Text fontSize="sm" color="purple.700">Fat: <b>{Math.round(pf)}g</b></Text>
                </HStack>
              </>
            )}
            <Divider />
            <Text fontSize="md" fontWeight="bold" color="gray.800">🏷️ Tags & Cautions</Text>
            <Wrap>
              {allTags.length > 0 ? (
                allTags.map(({ tag, color }, i) => (
                  <WrapItem key={i}>
                    <Badge colorScheme={color} borderRadius="md" px={2} fontSize="xs" boxShadow="sm">{tag}</Badge>
                  </WrapItem>
                ))
              ) : (
                <Text fontSize="sm" color="gray.500">No tags available</Text>
              )}
            </Wrap>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default MealInfoModal;


